import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  ChevronDown, Save, CheckCircle2, Languages, FileText, Plus, Trash2,
  ChevronsDownUp, ChevronsUpDown,
} from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { createMaterial, updateMaterial, checkDuplicates, uploadAttachment } from './materialApi';
import CodedSelect from './CodedSelect';
import DuplicateDialog from './DuplicateDialog';
import MaterialAttachments from './MaterialAttachments';

/**
 * MaterialCaptureForm — die eigentliche Erfassungsmaske.
 *
 * Prinzip „simpel": EINE Seite, Sektionen pro SAP-Sicht, nur AKTIVE Felder werden
 * gerendert. Pflichtkern (core) ist immer dabei. Pflicht wird nur beim „Anlegen"
 * (status=ready) erzwungen; „Als Entwurf speichern" (draft) laesst Luecken zu.
 *
 * Zusatz: Fuellstand (belegte/aktive Felder) + alles ein-/ausklappen, codierte
 * Selects (Schluessel prominent), SAP-Langtexte (Grunddaten-/Bestelltext …).
 */
const MaterialCaptureForm = ({ user, config, initial, onSaved, onCreated, showSuccess, showError }) => {
  const { isDarkMode } = useTheme();
  const { t, language } = useLanguage();
  const lang = language === 'en' ? 'en' : 'de';

  const enabledFields = useMemo(() => (config?.fields || []).filter((f) => f.enabled), [config]);
  const textTypes = config?.textTypes || [];

  const sections = useMemo(() => {
    const byView = {};
    for (const f of enabledFields) (byView[f.view] ||= []).push(f);
    return (config?.views || [])
      .map((v) => ({ ...v, fields: byView[v.key] || [] }))
      .filter((v) => v.fields.length > 0);
  }, [enabledFields, config]);

  const [values, setValues] = useState({});
  const [textEn, setTextEn] = useState('');
  const [showEn, setShowEn] = useState(false);
  const [longtexts, setLongtexts] = useState([]); // [{ textId, content }]
  const [collapsed, setCollapsed] = useState({});
  const [saving, setSaving] = useState(false);
  const [missing, setMissing] = useState(new Set());
  const [dupCandidates, setDupCandidates] = useState(null); // !=null => Dialog offen
  const [stagedFiles, setStagedFiles] = useState([]); // vorgemerkte PDFs (neues Material)

  // Initial-/Vorlagenwerte uebernehmen
  useEffect(() => {
    setStagedFiles([]);
    if (!initial) { setValues({}); setTextEn(''); setLongtexts([]); return; }
    const m = initial;
    const v = { ...(m.fields || {}) };
    ['matnr', 'mbrsh', 'mtart', 'maktx', 'meins', 'matkl', 'werks', 'vkorg', 'vtweg'].forEach((k) => {
      if (m[k] != null) v[k] = m[k];
    });
    setValues(v);
    setTextEn(m.texts?.en || '');
    setShowEn(!!m.texts?.en);
    setLongtexts(Array.isArray(m.longtexts) ? m.longtexts.map((x) => ({ textId: x.textId, lang: x.lang || 'DE', content: x.content || '' })) : []);
  }, [initial]);

  const setVal = useCallback((key, val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
    setMissing((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev); next.delete(key); return next;
    });
  }, []);

  const toggleSection = (key) => setCollapsed((p) => ({ ...p, [key]: !p[key] }));
  const collapseAll = () => setCollapsed(Object.fromEntries(sections.map((s) => [s.key, true])));
  const expandAll = () => setCollapsed({});

  const labelOf = (f) => (lang === 'en' ? f.labelEn : f.labelDe);

  // Fuellstand: belegte aktive Felder / aktive Felder
  const isFilled = (f) => {
    const v = values[f.key];
    if (f.type === 'checkbox') return v === true;
    return v != null && !(typeof v === 'string' && v.trim() === '');
  };
  const filledCount = useMemo(() => enabledFields.filter(isFilled).length, [enabledFields, values]); // eslint-disable-line
  const totalCount = enabledFields.length;
  const pct = totalCount ? Math.round((filledCount / totalCount) * 100) : 0;

  // -- Styles --
  const cardCls = isDarkMode ? 'bg-gray-900/30 border border-white/10 backdrop-blur-xl' : 'bg-white border border-slate-200';
  const inputBase = isDarkMode
    ? 'bg-white/5 border-white/15 text-white placeholder-white/30 focus:border-emerald-400/60'
    : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400 focus:border-emerald-500';
  const labelCls = isDarkMode ? 'text-white/70' : 'text-slate-600';
  const inputCls = (key, invalid) =>
    `w-full rounded-lg border px-3 py-2 text-sm transition-colors outline-none ${inputBase} ${invalid ? '!border-red-500 ring-1 ring-red-500/40' : ''}`;
  const smallBtn = isDarkMode
    ? 'inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-white/70 hover:text-white hover:bg-white/10'
    : 'inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-slate-500 hover:bg-slate-100';

  // -- Feld-Renderer --
  const renderField = (f) => {
    const invalid = missing.has(f.key);
    const val = values[f.key];

    let control;
    if (f.type === 'select') {
      control = (
        <CodedSelect value={val ?? ''} onChange={(v) => setVal(f.key, v)} options={f.options || []}
          lang={lang} placeholder={t('material.selectPlaceholder')} invalid={invalid} />
      );
    } else if (f.type === 'checkbox') {
      control = (
        <label className={`flex items-center gap-2 text-sm ${labelCls} cursor-pointer h-[38px]`}>
          <input type="checkbox" checked={!!val} onChange={(e) => setVal(f.key, e.target.checked)}
            className="w-4 h-4 rounded accent-emerald-500" />
          {t('material.yes')}
        </label>
      );
    } else if (f.type === 'textarea') {
      control = (
        <textarea className={inputCls(f.key, invalid)} rows={2} value={val ?? ''} onChange={(e) => setVal(f.key, e.target.value)} />
      );
    } else {
      control = (
        <input className={inputCls(f.key, invalid)} type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
          value={val ?? ''} onChange={(e) => setVal(f.key, e.target.value)} />
      );
    }

    return (
      <div key={f.key} className="flex flex-col gap-1">
        <label className={`text-xs font-medium ${labelCls} flex items-center gap-1`}>
          {labelOf(f)}
          {f.required && <span className="text-red-500">*</span>}
          <span className={`ml-1 font-mono text-[10px] ${isDarkMode ? 'text-white/25' : 'text-slate-400'}`}>{f.sap}</span>
        </label>
        {f.key === 'maktx' ? (
          <div className="flex flex-col gap-1.5">
            {control}
            {showEn && (
              <input className={inputCls('maktx_en', false)} type="text" placeholder={t('material.descEn')}
                value={textEn} onChange={(e) => setTextEn(e.target.value)} />
            )}
            <button type="button" onClick={() => setShowEn((s) => !s)}
              className={`self-start inline-flex items-center gap-1 text-[11px] ${isDarkMode ? 'text-emerald-300/80 hover:text-emerald-200' : 'text-emerald-600 hover:text-emerald-700'}`}>
              <Languages className="w-3 h-3" /> {showEn ? t('material.hideEn') : t('material.addEn')}
            </button>
          </div>
        ) : control}
      </div>
    );
  };

  // -- Langtexte --
  const usedTypeIds = longtexts.map((l) => l.textId);
  const nextType = () => (textTypes.find((tt) => !usedTypeIds.includes(tt.id)) || textTypes[0])?.id || 'GRUN';
  const addLongtext = () => setLongtexts((p) => [...p, { textId: nextType(), lang: lang.toUpperCase(), content: '' }]);
  const updLongtext = (i, patch) => setLongtexts((p) => p.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const delLongtext = (i) => setLongtexts((p) => p.filter((_, idx) => idx !== i));

  // -- Speichern --
  const submit = async (status) => {
    setMissing(new Set());
    if (status === 'ready') {
      const miss = new Set();
      for (const f of enabledFields) {
        if (f.required && !isFilled(f)) miss.add(f.key);
      }
      if (miss.size) { setMissing(miss); showError(t('material.missingRequired')); return; }

      // Optionale Dublettenpruefung VOR dem Anlegen (nur wenn Admin aktiviert).
      // Buttons waehrend der Pruefung sperren (In-Flight-Schutz).
      if (config?.settings?.dupcheckEnabled) {
        setSaving(true);
        try {
          const { candidates } = await checkDuplicates(user, {
            maktx: values.maktx, ean11: values.ean11, bismt: values.bismt, matkl: values.matkl,
            // NUR das eigene (bearbeitete) Material ausschließen — die Vorlage NICHT:
            // aus einer Vorlage anzulegen ist genau der Doppelanlage-Fall.
            excludeId: initial?.id || null,
          });
          if (Array.isArray(candidates) && candidates.length) {
            setDupCandidates(candidates); // Dialog oeffnen; Speichern wartet auf Bestaetigung
            return;
          }
        } catch { /* Pruefung darf das Anlegen nicht blockieren */ }
        finally { setSaving(false); }
      }
    }
    await doSave(status);
  };

  const doSave = async (status) => {
    const payload = {
      fields: values,
      texts: { de: values.maktx || '', en: textEn || '' },
      longtexts: longtexts
        .filter((l) => l.textId && (l.content || '').trim() !== '')
        .map((l) => ({ textId: l.textId, lang: (l.lang || lang).toUpperCase(), content: l.content })),
      status,
      reference_matnr: initial?.reference_matnr || values.reference_matnr || null,
    };
    setSaving(true);
    try {
      if (initial?.id) {
        await updateMaterial(user, initial.id, payload);
        showSuccess(status === 'ready' ? t('material.savedReady') : t('material.savedDraft'));
        onSaved?.();
      } else {
        const res = await createMaterial(user, payload);
        showSuccess(status === 'ready' ? t('material.savedReady') : t('material.savedDraft'));
        // Vorgemerkte PDF-Anhaenge jetzt hochladen (Material hat erst jetzt eine id)
        if (res?.id && stagedFiles.length) {
          for (const f of stagedFiles) {
            try { await uploadAttachment(user, res.id, f); } catch { /* einzelner Upload-Fehler bricht das Anlegen nicht ab */ }
          }
          setStagedFiles([]);
        }
        // Nach dem Anlegen im Bearbeiten-Modus bleiben, damit weitere PDF-Anhaenge moeglich sind.
        onCreated?.(res?.id);
      }
    } catch (e) {
      showError(t('material.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (!enabledFields.length) {
    return (
      <div className={`rounded-2xl p-8 text-center text-sm ${cardCls} ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
        {t('material.noFieldsConfigured')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Materialnummer + Fuellstand + Ein-/Ausklappen */}
      <div className={`rounded-2xl p-4 ${cardCls}`}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-col gap-1 min-w-[200px]">
            <label className={`text-xs font-medium ${labelCls}`}>{t('material.matnr')}</label>
            <input className={inputCls('matnr', false)} type="text" placeholder={t('material.matnrAuto')}
              value={values.matnr ?? ''} onChange={(e) => setVal('matnr', e.target.value)} />
            {initial?.reference_matnr && (
              <div className={`text-[11px] mt-0.5 ${isDarkMode ? 'text-emerald-300/80' : 'text-emerald-600'}`}>
                {t('material.fromTemplate')}: <span className="font-mono">{initial.reference_matnr}</span>
              </div>
            )}
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="flex items-center justify-between mb-1">
              <span className={`text-xs font-medium ${labelCls}`}>
                {t('material.fillProgress', { filled: filledCount, total: totalCount })}
              </span>
              <div className="flex items-center gap-1">
                <button type="button" onClick={collapseAll} className={smallBtn}><ChevronsDownUp className="w-3.5 h-3.5" />{t('material.collapseAll')}</button>
                <button type="button" onClick={expandAll} className={smallBtn}><ChevronsUpDown className="w-3.5 h-3.5" />{t('material.expandAll')}</button>
              </div>
            </div>
            <div className={`h-1.5 rounded-full overflow-hidden ${isDarkMode ? 'bg-white/10' : 'bg-slate-200'}`}>
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>
      </div>

      {/* Sektionen pro SAP-Sicht */}
      {sections.map((sec) => {
        const isCollapsed = !!collapsed[sec.key];
        const secFilled = sec.fields.filter(isFilled).length;
        return (
          <div key={sec.key} className={`rounded-2xl ${cardCls} overflow-hidden`}>
            <button type="button" onClick={() => toggleSection(sec.key)}
              className={`w-full flex items-center justify-between px-4 py-3 ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
              <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                {lang === 'en' ? sec.en : sec.de}
                <span className={`ml-2 text-xs font-normal ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{secFilled}/{sec.fields.length}</span>
              </span>
              <ChevronDown className={`w-4 h-4 transition-transform ${isCollapsed ? '-rotate-90' : ''} ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`} />
            </button>
            {!isCollapsed && (
              <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
                {sec.fields.map(renderField)}
              </div>
            )}
          </div>
        );
      })}

      {/* Texte (SAP-Langtexte) */}
      <div className={`rounded-2xl ${cardCls} overflow-hidden`}>
        <div className={`flex items-center justify-between px-4 py-3 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
          <span className="text-sm font-semibold flex items-center gap-2">
            <FileText className="w-4 h-4" /> {t('material.texts')}
            <span className={`text-xs font-normal ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{longtexts.length}</span>
          </span>
          <button type="button" onClick={addLongtext} disabled={!textTypes.length}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-emerald-600 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-500/10">
            <Plus className="w-3.5 h-3.5" /> {t('material.addText')}
          </button>
        </div>
        {longtexts.length > 0 && (
          <div className="px-4 pb-4 space-y-3">
            {longtexts.map((lt, i) => (
              <div key={i} className={`rounded-xl p-3 ${isDarkMode ? 'bg-white/5' : 'bg-slate-50'}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <select value={lt.textId} onChange={(e) => updLongtext(i, { textId: e.target.value })}
                    className={`rounded-lg border px-2 py-1 text-xs outline-none ${inputBase}`}>
                    {textTypes.map((tt) => (
                      <option key={tt.id} value={tt.id}>{tt.id} – {lang === 'en' ? tt.en : tt.de}</option>
                    ))}
                  </select>
                  <button type="button" onClick={() => delLongtext(i)}
                    className={`p-1.5 rounded-lg ${isDarkMode ? 'text-red-300/70 hover:text-red-300 hover:bg-red-500/10' : 'text-red-500 hover:bg-red-50'}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <textarea className={inputCls(`lt-${i}`, false)} rows={3} value={lt.content}
                  onChange={(e) => updLongtext(i, { content: e.target.value })} placeholder={t('material.textPlaceholder')} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* PDF-Anhaenge (bei neuem Material vorgemerkt, nach dem Anlegen hochgeladen) */}
      <MaterialAttachments user={user} materialId={initial?.id || null}
        staged={stagedFiles} onStaged={setStagedFiles}
        showSuccess={showSuccess} showError={showError} />

      {/* Aktionen */}
      <div className="flex items-center justify-end gap-3 pt-1 pb-8">
        <button type="button" disabled={saving} onClick={() => submit('draft')}
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all disabled:opacity-50 ${
            isDarkMode ? 'bg-white/10 hover:bg-white/15 text-white border border-white/10' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
          }`}>
          <Save className="w-4 h-4" /> {t('material.saveDraft')}
        </button>
        <button type="button" disabled={saving} onClick={() => submit('ready')}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 transition-all disabled:opacity-50 shadow-lg shadow-emerald-600/20">
          <CheckCircle2 className="w-4 h-4" /> {initial?.id ? t('material.update') : t('material.create')}
        </button>
      </div>

      {/* Dubletten-Dialog (optional) */}
      {dupCandidates && (
        <DuplicateDialog
          candidates={dupCandidates}
          onCancel={() => setDupCandidates(null)}
          onConfirm={() => { setDupCandidates(null); doSave('ready'); }}
        />
      )}
    </div>
  );
};

export default MaterialCaptureForm;
